import { useEffect, useState, useCallback } from "react";
import { MapContainer, TileLayer, Marker, Polyline, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Crosshair, MagnifyingGlass, MapPin } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const KL_CENTER = [3.139, 101.6869];

const fromIcon = L.divIcon({
  className: "",
  html: '<div style="width:22px;height:22px;border-radius:50%;background:#F59E0B;border:3px solid #0A0A0A;box-shadow:0 0 0 2px #F59E0B"></div>',
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});
const toIcon = L.divIcon({
  className: "",
  html: '<div style="width:22px;height:22px;border-radius:50%;background:#10B981;border:3px solid #0A0A0A;box-shadow:0 0 0 2px #10B981"></div>',
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

function ClickHandler({ activeField, onPick }) {
  useMapEvents({
    click(e) {
      onPick(activeField, { lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return null;
}

function Recenter({ points }) {
  const map = useMap();
  useEffect(() => {
    const pts = [points.from, points.to].filter(Boolean);
    if (pts.length === 2) {
      map.fitBounds(L.latLngBounds(pts.map((p) => [p.lat, p.lng])), { padding: [40, 40] });
    } else if (pts.length === 1) {
      map.setView([pts[0].lat, pts[0].lng], 14);
    }
  }, [points, map]);
  return null;
}

export default function MapPicker({ from, to, distanceKm, onChange }) {
  const [activeField, setActiveField] = useState("from");
  const [search, setSearch] = useState("");
  const [searching, setSearching] = useState(false);
  const [route, setRoute] = useState(null);
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    if (search.trim().length < 3) {
      setSuggestions([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(search)}&countrycodes=my&limit=5`);
        setSuggestions(await r.json());
      } catch {
        setSuggestions([]);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const pickSuggestion = (s) => {
    onChange("to", { lat: parseFloat(s.lat), lng: parseFloat(s.lon), label: s.display_name });
    setSearch("");
    setSuggestions([]);
    toast.success("Destination pinned");
  };

  const reverseGeocode = useCallback(async (lat, lng) => {
    try {
      const r = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`);
      const j = await r.json();
      return j.display_name || `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
    } catch {
      return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
    }
  }, []);

  const handlePick = useCallback(async (field, latlng) => {
    const label = await reverseGeocode(latlng.lat, latlng.lng);
    onChange(field, { ...latlng, label });
  }, [onChange, reverseGeocode]);

  const locateMe = () => {
    if (!navigator.geolocation) {
      toast.error("GPS not supported on this device");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const latlng = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        const label = await reverseGeocode(latlng.lat, latlng.lng);
        onChange("from", { ...latlng, label });
        toast.success("Location pinned from GPS");
      },
      () => toast.error("Unable to get GPS location"),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const searchPlace = async () => {
    if (!search.trim()) return;
    setSearching(true);
    try {
      const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(search)}&countrycodes=my&limit=1`);
      const j = await r.json();
      if (!j.length) {
        toast.error("Place not found. Try another name.");
        return;
      }
      const latlng = { lat: parseFloat(j[0].lat), lng: parseFloat(j[0].lon), label: j[0].display_name };
      onChange("to", latlng);
      toast.success("Destination pinned");
    } catch {
      toast.error("Search failed");
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    if (!from || !to) {
      setRoute(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`https://router.project-osrm.org/route/v1/driving/${from.lng},${from.lat};${to.lng},${to.lat}?overview=full&geometries=geojson`);
        const j = await r.json();
        if (cancelled || !j.routes?.length) return;
        const km = j.routes[0].distance / 1000;
        setRoute(j.routes[0].geometry.coordinates.map(([lng, lat]) => [lat, lng]));
        onChange("distance", km);
      } catch {
        if (!cancelled) setRoute(null);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from?.lat, from?.lng, to?.lat, to?.lng]);

  return (
    <div className="space-y-4" data-testid="map-picker">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">From (Pickup)</span>
            <Button type="button" size="sm" variant="outline" data-testid="locate-me-button" onClick={locateMe}
              className="rounded-sm border-primary/50 text-primary hover:bg-primary/10 hover:text-amber-400">
              <Crosshair size={14} className="mr-1" /> Locate Me
            </Button>
          </div>
          <div
            data-testid="from-field"
            onClick={() => setActiveField("from")}
            className={`cursor-pointer rounded-sm border px-4 py-3 text-sm transition-colors ${activeField === "from" ? "border-primary bg-primary/5" : "border-input bg-card"}`}
          >
            {from ? <span className="text-foreground">{from.label}</span> : <span className="text-muted-foreground">Tap map or use Locate Me</span>}
          </div>
        </div>
        <div className="space-y-2">
          <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">To (Drop-off)</span>
          <div className="relative flex gap-2">
            <div className="relative flex-1">
              <Input
                data-testid="map-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), searchPlace())}
                placeholder="Search place name (e.g. Bangsar)"
                className="rounded-sm border-input bg-card focus-visible:ring-primary"
              />
              {suggestions.length > 0 && (
                <ul data-testid="to-suggestions" className="absolute left-0 right-0 top-full z-[1100] mt-1 max-h-56 overflow-y-auto rounded-sm border border-border bg-popover shadow-xl">
                  {suggestions.map((s) => (
                    <li key={s.place_id}>
                      <button type="button" data-testid={`to-suggestion-${s.place_id}`} onClick={() => pickSuggestion(s)}
                        className="w-full border-b border-border/50 px-3 py-2.5 text-left text-xs transition-colors last:border-0 hover:bg-primary/10 hover:text-primary">
                        {s.display_name}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <Button type="button" data-testid="map-search-button" onClick={searchPlace} disabled={searching}
              className="rounded-sm bg-primary text-primary-foreground hover:bg-amber-400 hover:text-black">
              <MagnifyingGlass size={16} />
            </Button>
          </div>
          <div
            data-testid="to-field"
            onClick={() => setActiveField("to")}
            className={`cursor-pointer rounded-sm border px-4 py-3 text-sm transition-colors ${activeField === "to" ? "border-emerald-500 bg-emerald-500/5" : "border-input bg-card"}`}
          >
            {to ? <span className="text-foreground">{to.label}</span> : <span className="text-muted-foreground">Search or tap map to set drop-off</span>}
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-sm border border-border">
        <MapContainer center={KL_CENTER} zoom={11} style={{ height: 320, width: "100%" }} data-testid="route-map">
          <TileLayer
            url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}"
            attribution='&copy; <a href="https://www.esri.com/">Esri</a> &copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> contributors'
            maxNativeZoom={16}
            maxZoom={19}
          />
          <TileLayer
            url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}"
            attribution=""
            maxNativeZoom={16}
            maxZoom={19}
          />
          <ClickHandler activeField={activeField} onPick={handlePick} />
          <Recenter points={{ from, to }} />
          {from && <Marker position={[from.lat, from.lng]} icon={fromIcon} />}
          {to && <Marker position={[to.lat, to.lng]} icon={toIcon} />}
          {route && <Polyline positions={route} pathOptions={{ color: "#F59E0B", weight: 4, opacity: 0.85 }} />}
        </MapContainer>
      </div>

      <div className="flex items-center gap-3 rounded-sm border border-border bg-card px-5 py-4">
        <MapPin size={20} className="text-primary" />
        <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Total Distance</span>
        <span data-testid="distance-display" className="ml-auto font-mono text-xl font-bold text-primary">
          {distanceKm > 0 ? `${distanceKm.toFixed(1)} KM` : "—"}
        </span>
      </div>
    </div>
  );
}
